import React from 'react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function TermsOfServicePage() {
  const lastUpdated = 'April 2, 2026';
  return <div className="bg-viridia-black min-h-screen pt-32 pb-24" data-id="element-2321">
      <SEOHead title="Terms of Service | Viridia Analytics" description="Terms of Service for Viridia Analytics. Review the terms governing our marketing services." canonical="/terms" data-id="element-2322" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2323">
        <ScrollReveal data-id="element-2324">
          <div className="text-center mb-16" data-id="element-2325">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" data-id="element-2326">
              Terms of Service
            </h1>
            <p className="text-gray-400" data-id="element-2327">Last Updated: {lastUpdated}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} data-id="element-2328">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 prose prose-invert max-w-none" data-id="element-2329">
            <div className="space-y-8 text-gray-300 leading-relaxed" data-id="element-2330">
              <section data-id="element-2331">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2332">
                  1. Acceptance of Terms
                </h2>
                <p data-id="element-2333">
                  By accessing our website or engaging Viridia Analytics ("we,"
                  "our," or "us") for services, you agree to be bound by these
                  Terms of Service. If you do not agree to these terms, please
                  do not use our services or website.
                </p>
              </section>

              <section data-id="element-2334">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2335">
                  2. Description of Services
                </h2>
                <p data-id="element-2336">
                  Viridia Analytics provides digital marketing services,
                  including but not limited to ad campaign management (Google,
                  Meta, Yelp), marketing automation setup, CRM integration, and
                  web design/SEO services. The specific scope of services will
                  be outlined in a separate agreement or proposal provided to
                  the client.
                </p>
              </section>

              <section data-id="element-2337">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2338">
                  3. SMS/Text Messaging Terms
                </h2>
                <p className="mb-2" data-id="element-2339">
                  By opting in to receive SMS communications from us, you agree
                  to the following:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-2340">
                  <li data-id="element-2341">
                    By providing your phone number and submitting a form or
                    booking request, you consent to receive automated SMS/text
                    messages from Viridia Analytics.
                  </li>
                  <li data-id="element-2342">
                    Messages may include appointment confirmations, reminders,
                    follow-ups, service updates, and promotional content.
                  </li>
                  <li data-id="element-2343">
                    Message frequency varies. Message and data rates may apply.
                  </li>
                  <li data-id="element-2344">
                    You may opt out at any time by replying{' '}
                    <strong data-id="element-2345">STOP</strong>. Reply <strong data-id="element-2346">HELP</strong> for help.
                  </li>
                  <li data-id="element-2347">
                    Opting out of SMS does not cancel any service agreement you
                    may have with us.
                  </li>
                  <li data-id="element-2348">
                    We are not responsible for charges from your mobile carrier.
                  </li>
                  <li data-id="element-2349">
                    SMS consent is not required as a condition of purchasing any
                    service.
                  </li>
                  <li data-id="element-2350">
                    Carriers are not liable for delayed or undelivered messages.
                  </li>
                </ul>
              </section>

              <section data-id="element-2351">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2352">
                  4. Client Responsibilities
                </h2>
                <p className="mb-2" data-id="element-2353">
                  To ensure the successful delivery of our services, you agree
                  to:
                </p>
                <ul className="list-disc pl-6 space-y-2" data-id="element-2354">
                  <li data-id="element-2355">
                    Provide timely access to necessary accounts, assets, and
                    information required for campaign execution.
                  </li>
                  <li data-id="element-2356">
                    Ensure that any materials provided to us do not infringe on
                    third-party intellectual property rights.
                  </li>
                  <li data-id="element-2357">
                    Comply with the terms and policies of third-party
                    advertising platforms (e.g., Google, Meta, Yelp).
                  </li>
                  <li data-id="element-2358">
                    Review and approve campaigns or materials in a timely manner
                    when requested.
                  </li>
                </ul>
              </section>

              <section data-id="element-2359">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2360">
                  5. Payment Terms
                </h2>
                <p data-id="element-2361">
                  Fees for our services are outlined in your specific service
                  agreement. Unless otherwise stated, invoices are due upon
                  receipt. We reserve the right to pause or suspend services if
                  payment is not received within the agreed-upon timeframe. Ad
                  spend (payments made directly to platforms like Google or
                  Meta) is separate from our management fees and is the sole
                  responsibility of the client.
                </p>
              </section>

              <section data-id="element-2362">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2363">
                  6. Intellectual Property
                </h2>
                <p data-id="element-2364">
                  Upon full payment for services rendered, clients retain
                  ownership of the final deliverables (e.g., website design, ad
                  copy) created specifically for them. Viridia Analytics retains
                  ownership of any proprietary tools, methodologies, or
                  pre-existing materials used in the delivery of services.
                </p>
              </section>

              <section data-id="element-2365">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2366">
                  7. Limitation of Liability
                </h2>
                <p data-id="element-2367">
                  While we strive to deliver the best possible results, Viridia
                  Analytics does not guarantee specific outcomes, such as a
                  certain number of leads or revenue, as these depend on various
                  market factors. In no event shall Viridia Analytics be liable
                  for any indirect, incidental, special, or consequential
                  damages arising out of or in connection with our services.
                </p>
              </section>

              <section data-id="element-2368">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2369">
                  8. Termination
                </h2>
                <p data-id="element-2370">
                  Either party may terminate the service agreement with written
                  notice as specified in the individual contract. Upon
                  termination, the client is responsible for payment for all
                  services rendered up to the termination date.
                </p>
              </section>

              <section data-id="element-2371">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2372">
                  9. Governing Law
                </h2>
                <p data-id="element-2373">
                  These Terms of Service shall be governed by and construed in
                  accordance with the laws of the State of Arizona, without
                  regard to its conflict of law provisions. Any legal action or
                  proceeding arising under these terms shall be brought
                  exclusively in the courts located in Phoenix, Arizona.
                </p>
              </section>

              <section data-id="element-2374">
                <h2 className="text-2xl font-bold text-white mb-4" data-id="element-2375">
                  10. Contact Information
                </h2>
                <p className="mb-2" data-id="element-2376">
                  If you have any questions about these Terms of Service, please
                  contact us:
                </p>
                <ul className="list-none space-y-1" data-id="element-2377">
                  <li data-id="element-2378">
                    <strong data-id="element-2379">Company:</strong> Viridia Analytics
                  </li>
                  <li data-id="element-2380">
                    <strong data-id="element-2381">Location:</strong> Phoenix, AZ
                  </li>
                  <li data-id="element-2382">
                    <strong data-id="element-2383">Email:</strong>{' '}
                    <a href="mailto:info@viridiaanalytics.com" className="text-viridia hover:underline" data-id="element-2384">
                      info@viridiaanalytics.com
                    </a>
                  </li>
                  <li data-id="element-2385">
                    <strong data-id="element-2386">Phone:</strong>{' '}
                    <a href="tel:6233000708" className="text-viridia hover:underline" data-id="element-2387">
                      (623) 300-0708
                    </a>
                  </li>
                </ul>
              </section>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </div>;
}